import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { reverse } from 'named-urls';

import Record from 'ui/Record/Record';

import routes from 'core/routes';
import { useNavBarHeight } from 'utils/hooks/navbar';

import { useStore } from 'utils/hooks/store';

import MainHeader from 'components/MainHeader';

import { FolderType } from 'utils/api/modules/entities/entities.types';
import { UuidableName } from 'utils/api/types';

import EmptyDocumentsMessage from './components/EmptyDocumentsMessage/EmptyDocumentsMessage';

const findFirstDocumentInTree = (folders: FolderType[]): UuidableName | null => {
  for (const folder of folders) {
    if (folder.documents.length > 0) {
      return folder.documents[0];
    }
    if (folder.subfolders.length > 0) {
      const documentInSubfolder = findFirstDocumentInTree(folder.subfolders);
      if (documentInSubfolder) {
        return documentInSubfolder;
      }
    }
  }
  return null;
};

const Documents: React.FunctionComponent = observer((): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const { folders } = useStore();
  const navigate = useNavigate();
  const location = useLocation();

  const folderIdFromUrl = new URLSearchParams(location.search).get('folder');

  React.useEffect(() => {
    if (folders.isLoaded && folderIdFromUrl) {
      folders.selectFolder(folderIdFromUrl);
    }
  }, [folders.isLoaded, folderIdFromUrl]);

  React.useEffect(() => {
    if (!folders.isLoaded) return;
    if (folderIdFromUrl) return;

    const firstDocument = findFirstDocumentInTree(folders.folders);

    if (firstDocument) {
      navigate(reverse(routes.dashboard.documents.details, { entityId: firstDocument.uuid }));
    }
  }, [folders.isLoaded, folders.folders, folderIdFromUrl]);

  const selectedFolder = folders.flatFolders.find((f) => f.uuid === folderIdFromUrl);

  const breadcrumbs = [{ label: 'Documents', link: routes.dashboard.documents.toString() }];

  if (selectedFolder) {
    breadcrumbs.push({
      label: selectedFolder?.name || '',
      link: routes.dashboard.documents.toString(),
    });
  }

  return (
    <Record
      Header={<MainHeader title="" breadcrumbs={breadcrumbs} readonlyBreadcrumbs />}
      Content={() => <EmptyDocumentsMessage withoutFolders={folders.flatFolders.length === 0} />}
      navBarHeight={navBarHeight}
    />
  );
});

export default Documents;
